'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Clock3, PlayCircle } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { HeroBanner } from '@/components/ui/HeroBanner';
import { SectionsBlock } from '@/components/cms/SectionRenderer';
import { getPage, type HeroBannerSettings, type Page, type SectionItemPayload } from '@/lib/cms';
import { resourceHubApi } from '@/lib/resource-hub-api';

type VideoCard = {
  id: string;
  title: string;
  summary: string;
  level: string;
  duration: string;
  href: string;
  cta: string;
};

function getString(input: unknown): string | null {
  return typeof input === 'string' && input.trim() ? input.trim() : null;
}

function mapItemToVideo(item: SectionItemPayload): VideoCard | null {
  const metadata = item.metadata ?? {};
  const title = getString(item.title);
  if (!title) return null;

  const summary = getString(item.description) || getString(item.subtitle) || 'Watch this guide to learn the workflow.';
  const duration = getString((metadata as Record<string, unknown>).duration) || getString(item.subtitle) || 'Quick guide';
  const level = getString((metadata as Record<string, unknown>).level) || 'Guide';
  const href = getString(item.button_link) || '/contact';
  const cta = getString(item.button_text) || 'Watch guide';

  return {
    id: String(item.id ?? title),
    title,
    summary,
    level,
    duration,
    href,
    cta,
  };
}

export default function VideosPage() {
  const [page, setPage] = useState<Page | null>(null);
  const [apiVideos, setApiVideos] = useState<VideoCard[]>([]);

  useEffect(() => {
    getPage('videos').then(setPage).catch(() => setPage(null));
    resourceHubApi
      .videos()
      .then((response) => {
        const rows = (response.data.data ?? []).map((item) => ({
          id: String(item.id),
          title: item.title,
          summary: item.description || 'Watch this guide to learn the workflow.',
          level: item.difficulty_level || 'Guide',
          duration: item.duration || 'Quick guide',
          href: `/videos/${item.slug}`,
          cta: 'Watch guide',
        }));
        setApiVideos(rows);
      })
      .catch(() => setApiVideos([]));
  }, []);

  const heroSection = page?.sections?.find((section) => section.type === 'hero_banner');
  const title = page?.title || 'Video Walkthrough';
  const description =
    page?.content ||
    'Video tutorials now live inside the Event Guide. Browse role-based walkthroughs for individuals, organizers, and organizations.';

  const cmsVideos = useMemo(() => {
    const items = (page?.sections ?? [])
      .filter((section) => section.type !== 'hero_banner')
      .flatMap((section) => section.items ?? []);

    return items.map(mapItemToVideo).filter((item): item is VideoCard => item !== null);
  }, [page]);

  const videos = apiVideos.length ? apiVideos : cmsVideos;

  return (
    <MainLayout>
      <div className="pb-20 space-y-10">
        {heroSection ? (
          <HeroBanner data={(heroSection.settings ?? {}) as HeroBannerSettings} />
        ) : (
          <section className="rounded-3xl border border-landing-border bg-gradient-to-br from-soot via-moss to-soot p-8 text-text-inverse sm:p-12">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-inverse/80">Event Guide Videos</p>
            <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-4xl">{title}</h1>
            <p className="mt-4 max-w-2xl text-sm text-text-inverse/85 sm:text-base">{description}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                href="/guides/organizers"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-plaster px-5 py-3 text-sm font-semibold text-soot"
              >
                Open Event Guide
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-xl border border-white/35 px-5 py-3 text-sm font-semibold text-text-inverse"
              >
                Request a walkthrough
              </Link>
            </div>
          </section>
        )}

        {page?.sections && page.sections.length > 0 && <SectionsBlock sections={page.sections} />}

        <section className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-landing-text">Video library</h2>
              <p className="mt-1 text-sm text-landing-muted">Add or edit videos from backend section items on the videos page.</p>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {videos.length === 0 && (
              <article className="rounded-2xl border border-landing-border bg-bg p-5 md:col-span-2 lg:col-span-3">
                <h3 className="text-base font-semibold text-landing-text">No videos published yet</h3>
                <p className="mt-1 text-sm text-landing-muted">Check back soon for new walkthroughs.</p>
              </article>
            )}
            {videos.map((video) => (
              <article key={video.id} className="rounded-2xl border border-landing-border bg-bg p-5">
                <div className="flex items-center justify-between gap-2 text-xs text-landing-muted">
                  <span className="rounded-full bg-landing-bg px-2.5 py-1 font-medium">{video.level}</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock3 className="h-3.5 w-3.5" />
                    {video.duration}
                  </span>
                </div>
                <div className="mt-4 flex items-start gap-3">
                  <div className="rounded-lg bg-landing-bg p-2 text-landing-primary">
                    <PlayCircle className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-landing-text">{video.title}</h3>
                    <p className="mt-1 text-sm text-landing-muted">{video.summary}</p>
                  </div>
                </div>
                <Link href={video.href} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-landing-primary">
                  {video.cta}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </section>
      </div>
    </MainLayout>
  );
}
